import { Table } from '@bordrio/ui-components';
import { useTranslation } from 'react-i18next';
import { Card } from '@src/components';
import InfoTooltip from '@src/components/InfoTooltip';
import PresignedDownloadButton from '@src/components/PresignedDownloadButton';
import { TruncatedText } from '@src/components/TruncatedText/TruncatedText';
import { formatDate } from '@src/utils/dates';

export interface DocumentEntry {
  id: string;
  createdAt: string;
  recipient: string;
  documentType: string;
  additionalInfo?: string;
  additionalInfoTooltip?: string;
  fileId: string;
}

interface DocumentsTableProps {
  className?: string;
  documents: DocumentEntry[];
}

export function DocumentDetail({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-w-[150px] justify-between">
      <strong>{title}</strong>
      <div className="text-right">{children}</div>
    </div>
  );
}

export function DocumentsTable({
  className,
  documents,
}: DocumentsTableProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <Table className="hidden md:block">
        <Table.Head>
          <Table.HeadRow>
            <Table.HeadCell>
              {t('documents.table_columns.date', 'Date')}
            </Table.HeadCell>
            <Table.HeadCell>
              {t('documents.table_columns.recipient', 'Recipient')}
            </Table.HeadCell>
            <Table.HeadCell>
              {t('documents.table_columns.type', 'Type')}
            </Table.HeadCell>
            <Table.HeadCell>
              {t('documents.table_columns.notes', 'Notes')}
            </Table.HeadCell>
            <Table.HeadCell>
              {t('documents.table_columns.download', 'Download')}
            </Table.HeadCell>
          </Table.HeadRow>
        </Table.Head>
        <Table.Body>
          {documents.map((document) => (
            <Table.Row key={document.id}>
              <Table.Cell>{formatDate(document.createdAt)}</Table.Cell>
              <Table.Cell>
                <TruncatedText capWidth="medium">
                  {document.recipient}
                </TruncatedText>
              </Table.Cell>
              <Table.Cell>{document.documentType}</Table.Cell>
              <Table.Cell className="flex items-center space-x-2">
                <TruncatedText capWidth="medium">
                  {document.additionalInfo}
                </TruncatedText>
                {document.additionalInfoTooltip && (
                  <InfoTooltip>
                    <div className="whitespace-pre-wrap">
                      {document.additionalInfoTooltip}
                    </div>
                  </InfoTooltip>
                )}
              </Table.Cell>
              <Table.Cell>
                <PresignedDownloadButton fileId={document.fileId} asLink>
                  {t('documents.download', 'Download')}
                </PresignedDownloadButton>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <div className="flex flex-col space-y-4 md:hidden">
        {documents.map((document) => (
          <Card key={document.id} padding className="space-y-4" fullWidth>
            <h3>{document.documentType}</h3>
            <DocumentDetail title={t('documents.card_label.date', 'Date')}>
              {formatDate(document.createdAt)}
            </DocumentDetail>
            <DocumentDetail
              title={t('documents.card_label.recipient', 'Recipient')}
            >
              <TruncatedText capWidth="medium">
                {document.recipient}
              </TruncatedText>
            </DocumentDetail>
            {document.additionalInfo && (
              <DocumentDetail title={t('documents.card_label.notes', 'Notes')}>
                <div className="flex items-center space-x-2">
                  <TruncatedText capWidth="medium">
                    {document.additionalInfo}
                  </TruncatedText>
                  {document.additionalInfoTooltip && (
                    <InfoTooltip>
                      <div className="whitespace-pre-wrap">
                        {document.additionalInfoTooltip}
                      </div>
                    </InfoTooltip>
                  )}
                </div>
              </DocumentDetail>
            )}
            <div>
              <PresignedDownloadButton
                className="w-full"
                fileId={document.fileId}
              >
                {t('documents.download', 'Download')}
              </PresignedDownloadButton>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
